import { syncThrottlingKey } from "@/types/products/product-keys";
import { reassignCacheOwner } from "./cache-maintenance";
import { CACHE_OWNER_KEY } from "./cache-schema";
import { syncState } from "./index";

/**
 * What the main thread's owner check found:
 * - 'none': nobody is signed in, or the token names no identity we recognise ("cannot tell" is never "different user");
 * - 'unchanged': the cache already belongs to this member;
 * - 'adopted': no owner was recorded (a fresh cache, or one written before ownership was tracked), now this member;
 * - 'reassigned': it belonged to someone else, whose entitlement-filtered rows are now gone.
 */
export type CacheOwnership = 'none' | 'unchanged' | 'adopted' | 'reassigned';

/** The check for the member signed in now, shared by every caller until the member or the session changes. */
let current: { owner: string, check: Promise< CacheOwnership > } | null = null;
/** Checks run one after another: a switch back and forth must end with the last member recorded. */
let queue: Promise< unknown > = Promise.resolve();

async function reconcile( owner: string ): Promise< CacheOwnership > {
    const recorded = await syncState.get< string >( CACHE_OWNER_KEY );
    if ( recorded === owner ) {
        return 'unchanged';
    }

    if ( null == recorded ) {
        await syncState.put( CACHE_OWNER_KEY, owner );
        return 'adopted';
    }

    // One transaction: the previous member's rows and cooldowns go and this member is recorded, or nothing changed.
    await reassignCacheOwner( owner );

    // The worker skips the pass cooldown when it finds the cache changed hands; found here first, it would not, and
    // the new member's first pass could wait out the previous member's. Best-effort: at worst it waits 30 s.
    try {
        await syncState.delete( syncThrottlingKey );
    } catch {
        /* the next pass runs after the cooldown instead */
    }

    return 'reassigned';
}

/**
 * UX roadmap #63 (boot-5): makes sure the cache belongs to `owner` (deriveCacheOwnerId of the token signed in now)
 * before the main thread reads a user-scoped store. Only the sync worker used to check, and it starts after the
 * boot has read the stores, so the next member on a shared browser was shown the previous member's entitlement-
 * filtered content until the worker's first pass. The worker keeps its own check (sync-supervisor.ts) as a backstop.
 *
 * Concurrent and later callers for the same owner share one check. Rejects with the CacheError that stopped it (a
 * CacheClosedError when a newer build owns the database); a failed check is forgotten, so the next caller retries.
 */
export function ensureCacheOwner( owner: string | null ): Promise< CacheOwnership > {
    if ( null == owner ) {
        return Promise.resolve( 'none' );
    }

    if ( current?.owner === owner ) {
        return current.check;
    }

    const check: Promise< CacheOwnership > = queue.catch( () => undefined ).then( () => reconcile( owner ) );
    const entry = { owner, check };
    current = entry;
    queue = check;
    check.catch( () => {
        if ( current === entry ) {
            current = null;
        }
    } );

    return check;
}

/**
 * Called when a session ends. The recorded owner can change while this tab is signed out (another tab signs someone
 * else in), so the next session here checks again rather than trusting the previous session's answer. A check still
 * running keeps its place in the queue.
 */
export function forgetCacheOwnerCheck(): void {
    current = null;
}
